import ChartCard from "../../components/ChartCard";
import StatTile from "../../components/StatTile";
import { EmptyChartNote } from "../../components/charts/StackedColumns";
import TrendLine from "../../components/charts/TrendLine";
import { CardGrid, NoteCard, Section } from "../../components/Layout";
import { formatDayTick, formatNumber } from "../../format";
import { mixpanelLink } from "../../mixpanel";
import { viz } from "../../styles";
import { EngagementStatsResponse } from "../../types";

/**
 * Acquisition — and the three different numbers that all sound like "users".
 *
 * "How many users do we have" has three defensible answers at Markit and they
 * differ by an order of magnitude each, so all three are on this section with
 * their labels doing real work:
 *
 *  1. **Firebase Auth accounts** (241,177) — every account creation. An Auth
 *     account is minted the first time anyone verifies a phone number, so this
 *     counts ticket buyers and subscribers alongside creators. It is the widest
 *     honest definition of "signed up". CHARTED.
 *  2. **Creator sign ups** (6,233) — accounts that finished Markit's onboarding
 *     flow AND signed up with an email address. ~2.6% of the above. This is the
 *     number to quote for "people who signed up to USE Markit". CHARTED.
 *  3. **Firestore `users` documents** (673,943) — audience REACH, not
 *     acquisition. A static tile, not a chart, and deliberately placed below
 *     the two series with its own explanation rather than beside them.
 *
 * **The email condition on #2 is new (Sep 2026) and it made the number much
 * smaller — 22,422 to 6,233.** The onboarding flag alone also matched
 * subscribers who finished setting up a subscriber account; Markit's signup
 * fork splits on contact method (creators by email, subscribers by SMS), so
 * requiring an email drops the subscriber half. It also drops ~3,290 real
 * phone-first creators, which is an accepted, documented undercount rather than
 * a bug — see the note card below and the backend README.
 *
 * Two charts rather than two series on one axis: the daily creator figure is
 * small against 84-382 total account creations, so a shared linear Y axis
 * flattens the creator line onto the baseline and makes the more
 * decision-relevant of the two series the unreadable one.
 *
 * Source choice for both: `AccountData` carries no creation timestamp at all —
 * verified field by field — so Firebase Auth's `metadata.creationTime` is not
 * merely the better source, it is the only one. The creator series is that same
 * Auth timestamp filtered to the Firestore-matched uids, which is what keeps
 * the two series on one clock and the creator line a true subset. #3 has no
 * timestamp available at all, which is why it can only ever be a static total.
 */
const SIGNUP_SERIES = [
  { key: "signups", label: "Accounts created", color: viz.series1 },
];

const CREATOR_SERIES = [
  { key: "creatorSignups", label: "Creator sign ups", color: viz.series3 },
];

const AcquisitionSection = ({
  engagement,
  engagementError,
  dimmed,
}: {
  engagement: EngagementStatsResponse | undefined;
  engagementError: string | undefined;
  dimmed: boolean;
}) => {
  const syncedAt = engagement?.computedAt ?? undefined;
  const days = engagement?.days ?? [];
  const totalSignups = days.reduce((sum, day) => sum + day.signups, 0);
  const totalCreatorSignups = days.reduce(
    (sum, day) => sum + day.creatorSignups,
    0
  );

  // Share of this range's account creations that went on to finish onboarding.
  // Suppressed rather than shown as "0%" when the range has no accounts at all,
  // which is a different statement from "nobody converted".
  const creatorShare =
    totalSignups > 0
      ? `${((totalCreatorSignups / totalSignups) * 100).toFixed(1)}% of accounts created`
      : "Share unavailable — no accounts in range";

  const totals = engagement?.totals;

  // Two separate reconciliations, each only stated when the rollup actually
  // recorded the figure behind it. A rollup written before the email
  // requirement shipped reports 0 for these, which means "not recorded" — so
  // they are omitted rather than rendered as "0 excluded", which would be a
  // claim the data does not support.
  const creatorAccountsTooltip = totals
    ? [
        totals.creatorRecordsWithoutEmail > 0
          ? `${formatNumber(
              totals.creatorRecordsFlagOnly
            )} accounts finished onboarding; ${formatNumber(
              totals.creatorRecordsWithoutEmail
            )} of them have no email address and are excluded, because subscribers sign up by phone while creators sign up by email. That deliberately also drops legacy creators who signed up phone-first.`
          : undefined,
        totals.creatorRecordsWithoutAuth > 0
          ? `${formatNumber(
              totals.creatorRecords
            )} Firestore records match both conditions; ${formatNumber(
              totals.creatorRecordsWithoutAuth
            )} of them no longer have a Firebase Auth account, so they carry no creation date and are not charted.`
          : undefined,
      ]
        .filter(Boolean)
        .join(" ")
    : undefined;

  // 0 is "the count failed, or predates this field", never an empty
  // collection — so it renders as a muted dash, the same contract the rest of
  // this dashboard uses for a figure that cannot be known.
  const userRecords = totals?.totalUserRecords ?? 0;

  // This tile runs on its OWN clock — weekly, not nightly like everything else
  // on this page — so it must never use `syncedAt` (the document's overall
  // rebuild time). Using that here would claim a freshness this figure does
  // not have.
  const userRecordsSyncedAt = totals?.totalUserRecordsComputedAt ?? undefined;

  return (
    <Section
      title="Acquisition"
      description="New accounts entering the platform, the narrower count of people who signed up to actually use Markit, and — separately — how many people Markit holds a record for at all."
    >
      <CardGrid min={220}>
        <StatTile
          label="Accounts created (range)"
          value={
            engagementError || !engagement ? "—" : formatNumber(totalSignups)
          }
          secondary="Every Firebase Auth account · includes ticket buyers"
          tone={engagementError ? "warning" : "neutral"}
          freshness={{ kind: "cached", updatedAt: syncedAt }}
        />
        <StatTile
          label="Creator sign ups (range)"
          value={
            engagementError || !engagement
              ? "—"
              : formatNumber(totalCreatorSignups)
          }
          secondary={
            engagementError || !engagement
              ? "Finished onboarding, with an email address"
              : `Finished onboarding + has an email · ${creatorShare}`
          }
          title="Two conditions: the account finished Markit's onboarding flow AND signed up with an email address. Creators sign up by email, subscribers by phone, so the email requirement excludes subscribers — at the cost of undercounting legacy phone-first creators."
          tone={engagementError ? "warning" : "neutral"}
          freshness={{ kind: "cached", updatedAt: syncedAt }}
        />
        <StatTile
          label="Total accounts, all time"
          value={totals ? formatNumber(totals.totalAccounts) : "—"}
          secondary="Every Firebase Auth account on the platform"
          freshness={{ kind: "cached", updatedAt: syncedAt }}
        />
        <StatTile
          label="Creator accounts, all time"
          value={totals ? formatNumber(totals.totalCreatorAccounts) : "—"}
          secondary={
            totals
              ? `${
                  totals.totalAccounts > 0
                    ? (
                        (totals.totalCreatorAccounts / totals.totalAccounts) *
                        100
                      ).toFixed(1)
                    : "—"
                }% of all accounts · onboarded + email`
              : "Finished onboarding, with an email address"
          }
          title={creatorAccountsTooltip}
          freshness={{ kind: "cached", updatedAt: syncedAt }}
        />
      </CardGrid>

      <div style={{ marginTop: 16 }}>
        <CardGrid min={340}>
          <ChartCard
            title="All account creations (Firebase Auth)"
            subtitle="Nightly rollup · every Firebase Auth account, by UTC day — creators and ticket buyers alike"
            series={SIGNUP_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "cached", updatedAt: syncedAt }}
            table={{
              categoryLabel: "Day",
              rows: days.map(day => ({
                category: day.date,
                values: { signups: day.signups },
              })),
            }}
          >
            {engagementError ? (
              <EmptyChartNote label={engagementError} />
            ) : days.length === 0 ? (
              <EmptyChartNote label="The nightly rollup has not written signups yet." />
            ) : (
              <TrendLine
                data={days}
                series={SIGNUP_SERIES}
                categoryKey="date"
                tickFormatter={formatDayTick}
                labelFormatter={formatDayTick}
              />
            )}
          </ChartCard>

          <ChartCard
            title="Creator sign ups (onboarded, with an email)"
            subtitle="Nightly rollup · the subset of the chart beside this one that completed signup AND used an email — creators sign up by email, subscribers by phone. Note the much smaller Y axis"
            series={CREATOR_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "cached", updatedAt: syncedAt }}
            table={{
              categoryLabel: "Day",
              rows: days.map(day => ({
                category: day.date,
                values: { creatorSignups: day.creatorSignups },
              })),
            }}
          >
            {engagementError ? (
              <EmptyChartNote label={engagementError} />
            ) : days.length === 0 ? (
              <EmptyChartNote label="The nightly rollup has not written signups yet." />
            ) : (
              <TrendLine
                data={days}
                series={CREATOR_SERIES}
                categoryKey="date"
                tickFormatter={formatDayTick}
                labelFormatter={formatDayTick}
              />
            )}
          </ChartCard>
        </CardGrid>
      </div>

      <div style={{ marginTop: 16 }}>
        <NoteCard title="Two numbers above, both of which could be called ‘users’ — read the label before quoting one">
          <p style={{ margin: "0 0 8px" }}>
            <strong>Accounts created</strong> is every Firebase Auth account.
            Markit mints one the first time anyone verifies a phone number, so
            this counts <em>ticket buyers and subscribers alongside creators</em>
            . It is the widest honest reading of &ldquo;signed up&rdquo;, and on
            its own it overstates how many people came to Markit to run
            something.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <strong>Creator sign ups</strong> is the narrower count, and it takes{" "}
            <strong>two</strong> conditions: the account finished
            Markit&rsquo;s onboarding flow (
            <code>haveNotFinishedInitialStartup === false</code>, flipped only
            when someone completes web or mobile setup)
            <em> and</em> it has a real email address. Quote this one for
            &ldquo;people who signed up to use Markit&rdquo;. It is deliberately
            not <code>inCreatorMode</code>, which measures a UI toggle.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Why the email condition, and what it costs.</strong> The
            onboarding flag on its own also matched <em>subscribers</em> who
            finished setting up a subscriber account. Markit&rsquo;s signup fork
            splits on contact method — creators sign up by email, subscribers by
            phone — and no subscriber, RSVP, import or ticket-buying path ever
            writes an email onto an account, so requiring one excludes them
            cleanly. The cost is that it also drops creators who signed up
            phone-first, which was common before 2025:{" "}
            <strong>22,422 accounts finished onboarding, 6,233 of them have an
            email</strong>, and about <strong>3,290</strong> of the 16,189
            excluded do look like real phone-era creators. That undercount is
            deliberate and accepted — it is concentrated in 2023-2024 signups
            (87% of it), and in 2026 it costs single digits. Treat this series as
            a clean <em>recent</em> signal and a conservative floor on the older
            years.
          </p>
        </NoteCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <CardGrid min={340}>
          <StatTile
            label="Audience reach (user records)"
            value={userRecords > 0 ? formatNumber(userRecords) : "—"}
            secondary="Every Firestore user record · refreshed weekly, not nightly"
            title={
              userRecords > 0
                ? "Every document in the Firestore users collection. Not a signup count and not comparable to the two figures above — a record is written the moment anyone is imported, RSVPs or buys a ticket, whether or not they ever signed up themselves. This scan is expensive, so it only runs about once a week — every other figure on this page updates nightly."
                : "This has not been computed yet."
            }
            freshness={{ kind: "cached", updatedAt: userRecordsSyncedAt }}
          />
          <NoteCard title="This one is reach, not acquisition — it is not meant to reconcile with the numbers above">
            <p style={{ margin: "0 0 8px" }}>
              Markit writes a full user record up front for{" "}
              <em>every imported contact, RSVP and event attendee</em>, and only
              creates a login account if that person later logs in. Roughly{" "}
              <strong>two thirds</strong> of these records belong to people with
              no Firebase Auth account at all — they never logged in and never
              chose to join anything.
            </p>
            <p style={{ margin: 0 }}>
              So this answers &ldquo;how many people do Markit&rsquo;s creators
              collectively hold a record for&rdquo; — a genuine measure of reach
              for an external conversation. It is <strong>not</strong> a signup
              number, it does <strong>not</strong> roll up to
              &ldquo;Accounts created&rdquo;, and it has no daily series because
              user records carry no creation timestamp. A person whose phone
              record went missing can end up with two records, so it is a slight
              over-count of humans. It is also on its own clock:{" "}
              <strong>this figure is refreshed about once a week, not nightly
              like every other number on this page</strong> — the full scan
              behind it is Markit&rsquo;s single largest Firestore read cost, and
              the count moves slowly enough that daily precision isn&rsquo;t
              worth it. The tile above shows exactly when it was last computed.
            </p>
          </NoteCard>
        </CardGrid>
      </div>

      <div style={{ marginTop: 16 }}>
        <NoteCard title="This is Markit's own count, not Mixpanel's ‘User Sign Up’">
          Both series above are reconstructed from Firebase Auth&rsquo;s
          account-creation timestamp, which is authoritative for when an account
          was created. Neither is a mirror of the Mixpanel board metric, and the
          figures may legitimately disagree — Mixpanel may
          filter test or bot accounts, dedupe differently, or count a signup{" "}
          <em>event</em> rather than an account. Quote these figures as
          &ldquo;Firebase Auth accounts created&rdquo; and &ldquo;creators who
          finished onboarding&rdquo;, never as the Mixpanel number.{" "}
          <a
            href={mixpanelLink.insights("32021611")}
            target="_blank"
            rel="noreferrer"
          >
            Compare in Mixpanel
          </a>
          .
        </NoteCard>
      </div>
    </Section>
  );
};

export default AcquisitionSection;
